import React from 'react';
import { FileText, AlignLeft } from 'lucide-react';

/**
 * Компонент для редактирования параметров текста
 */
const TextParametersSection = ({ formData, handleInputChange, errors }) => {
  // Варианты выравнивания текста
  const textAlignOptions = [
    { value: 'left', label: 'По левому краю', icon: 'align-left' },
    { value: 'center', label: 'По центру', icon: 'align-center' },
    { value: 'right', label: 'По правому краю', icon: 'align-right' },
    { value: 'justify', label: 'По ширине', icon: 'align-justify' }
  ];

  return (
    <div className="mb-6">
      <div className="mb-4">
        <label htmlFor="textContent" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <FileText className="w-4 h-4 mr-2 text-indigo-500" />
          Текст для просмотра
        </label>
        <textarea
          id="textContent"
          name="textContent"
          value={formData.textContent}
          onChange={handleInputChange}
          rows={3}
          className={`form-textarea w-full ${errors.textContent ? 'input-error' : ''}`}
          placeholder="Введите текст для проверки размера и удобочитаемости"
        />
        {errors.textContent && (
          <p className="mt-1 text-xs text-red-500 dark:text-red-400">
            {errors.textContent}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Цветовая схема */}
        <div className="col-span-full">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Цветовая схема
          </label>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="colorScheme"
                value="light"
                checked={formData.colorScheme === 'light'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                <span className="w-4 h-4 rounded-full inline-block mr-1.5 bg-white border border-gray-300"></span>
                Светлая
              </span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="colorScheme"
                value="dark"
                checked={formData.colorScheme === 'dark'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                <span className="w-4 h-4 rounded-full inline-block mr-1.5 bg-gray-800 border border-gray-700"></span>
                Темная
              </span>
            </label>
          </div>
        </div>

        {/* Выравнивание текста */}
        <div className="col-span-full">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            <AlignLeft className="w-4 h-4 mr-2 text-indigo-500" />
            Выравнивание текста
          </label>
          <div className="grid grid-cols-4 gap-2">
            {textAlignOptions.map((option) => (
              <label 
                key={option.value}
                className={`
                  flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer 
                  ${formData.textAlign === option.value 
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800' 
                    : 'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                  }
                `}
              >
                <input
                  type="radio"
                  name="textAlign"
                  value={option.value}
                  checked={formData.textAlign === option.value}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <span className="text-sm text-center text-gray-700 dark:text-gray-300">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Тип медиа */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип отображения
          </label>
          <div className="flex items-center space-x-4">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="mediaType"
                value="screen"
                checked={formData.mediaType === 'screen'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Экран
              </span>
            </label>
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="radio"
                name="mediaType"
                value="print"
                checked={formData.mediaType === 'print'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Печать
              </span>
            </label>
          </div>
        </div>

        {/* Расстояние просмотра */}
        <div>
          <label htmlFor="viewingDistance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Расстояние просмотра (см)
          </label>
          <input
            type="text"
            id="viewingDistance"
            name="viewingDistance"
            value={formData.viewingDistance}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.viewingDistance ? 'input-error' : ''}`}
            placeholder="Расстояние до экрана"
          />
          {errors.viewingDistance && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.viewingDistance}
            </p>
          )}
        </div>

        {/* Настройки доступности */}
        <div className="col-span-full">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="includeAccessibility"
              name="includeAccessibility"
              checked={formData.includeAccessibility}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <label htmlFor="includeAccessibility" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              Учитывать требования доступности
            </label>
          </div>
          {formData.includeAccessibility && (
            <div className="mt-3 pl-6">
              <label htmlFor="fontScale" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Масштаб шрифта (%)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  id="fontScale"
                  name="fontScale"
                  min="75"
                  max="200"
                  step="5"
                  value={formData.fontScale}
                  onChange={handleInputChange}
                  className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-12 text-center">
                  {formData.fontScale}%
                </span>
              </div>
              {errors.fontScale && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.fontScale}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextParametersSection;